import React from "react";
import Section from "../Section/Section";
import { useServices } from "../../services/Services";
import { Osdk } from "@osdk/client";
import { Event as EventObject } from "@unruly-osdk/sdk";
import PillsGroup from "../PillsGroup/PillsGroup";
import { PillsVariant } from "../../common-types/Pill";
import {
  formatDate,
  getDomainFromUrl,
  getInternalSourceLabel,
  isInternalEvent,
} from "../../helper/helper";
import styles from "./EventSummary.module.css";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import EventIcon from "@mui/icons-material/Event";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";

interface EventSummaryProps {
  event: Osdk.Instance<EventObject>;
  onLocationPillClick?: (id: string, type: string) => void;
}

const getImpactLevelTagVariant = (impactLevel: number): PillsVariant => {
  if (impactLevel >= 2 && impactLevel < 3) return PillsVariant.warning;
  if (impactLevel >= 3 && impactLevel <= 4) return PillsVariant.danger;
  return PillsVariant.default;
};

const EventSummary: React.FC<EventSummaryProps> = ({ event }) => {
  const services = useServices();
  const { data: country, isFetched: isCountryFetched } =
    services.Event.useGetCountry(event);

  const { startDate, impactLevel, name, newsSourceUrl } = event;
  const isInternalEventEvent = isInternalEvent(event.sourceType);
  const domain = newsSourceUrl ? getDomainFromUrl(newsSourceUrl) : undefined;
  const sourceLabel = isInternalEventEvent
    ? getInternalSourceLabel(event.sourceType)
    : (domain ?? "No source");
  const sourceHref = isInternalEventEvent
    ? undefined
    : newsSourceUrl || undefined;

  const eventDate = startDate ? formatDate(startDate) : "-";

  const impactLevelVariant = getImpactLevelTagVariant(impactLevel ?? 0);

  const pills = [
    {
      variant: PillsVariant.default,
      value: eventDate,
      label: "Date",
      icon: EventIcon,
    },
    {
      variant: PillsVariant.default,
      value: isCountryFetched ? (country?.name ?? "No Country") : "...",
      label: "Location",
      id: country?.countryId,
      type: "country",
      icon: LocationOnOutlinedIcon,
    },
    {
      variant: impactLevelVariant,
      value: impactLevel ? `${impactLevel}` : "",
      label: "General Impact Level",
      tooltipText:
        "Impact levels gauge an event's influence on global politics, from localized effects (Level 1) to transformative shifts in the international political order (Level 4)",
      icon: ErrorOutlineIcon,
    },
    {
      variant: PillsVariant.default,
      value: sourceLabel,
      label: "Source",
      href: sourceHref,
      icon: NewspaperIcon,
    },
  ];

  return (
    <Section>
      <h2 className={styles["title"]}>{name}</h2>
      <PillsGroup pills={pills} />
    </Section>
  );
};

export default EventSummary;
